/**
 * Slider: how many square KPI tiles sit on one row.
 * The grid updates while dragging; the value is saved when the user releases.
 */

import React, { useEffect, useState } from 'react';
import { KPI_ROW_COLUMNS, type KpiRowColumns } from './types';

interface ColumnsControlsProps {
  columns: number;
  saving: boolean;
  onPreview: (columns: KpiRowColumns) => void;
  onCommit: (columns: KpiRowColumns) => void;
  t: (key: string) => string;
}

const MIN = KPI_ROW_COLUMNS[0];
const MAX = KPI_ROW_COLUMNS[KPI_ROW_COLUMNS.length - 1];

function asColumns(value: number): KpiRowColumns {
  const snapped = Math.min(MAX, Math.max(MIN, Math.round(value)));
  return snapped as KpiRowColumns;
}

export const ColumnsControls: React.FC<ColumnsControlsProps> = ({
  columns,
  saving,
  onPreview,
  onCommit,
  t
}) => {
  const [draft, setDraft] = useState<KpiRowColumns>(asColumns(columns));

  useEffect(() => {
    setDraft(asColumns(columns));
  }, [columns]);

  const apply = (value: number, commit: boolean) => {
    const next = asColumns(value);
    setDraft(next);
    onPreview(next);
    if (commit && next !== columns) onCommit(next);
  };

  return (
    <div className="mb-2">
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <p className="text-[10px] text-[var(--color-secondary-text)]">
          {t('kpi.perfColumnsLabel') || 'KPIs per row'}
        </p>
        <span className="text-[11px] font-medium text-[var(--color-surface-text)] tabular-nums">{draft}</span>
      </div>
      <input
        type="range"
        min={MIN}
        max={MAX}
        step={1}
        disabled={saving}
        value={draft}
        aria-label={t('kpi.perfColumnsLabel') || 'KPIs per row'}
        className="w-full h-4 cursor-pointer accent-[var(--color-accent-bg)]"
        onChange={(event) => apply(Number(event.target.value), false)}
        onPointerUp={(event) => apply(Number((event.target as HTMLInputElement).value), true)}
        onKeyUp={(event) => apply(Number((event.target as HTMLInputElement).value), true)}
      />
      <div className="flex justify-between text-[10px] text-[var(--color-secondary-text)] px-0.5">
        {KPI_ROW_COLUMNS.map((count) => (
          <span key={count}>{count}</span>
        ))}
      </div>
    </div>
  );
};
